import { tamanoDelNumero } from '../../lib/tamanoDelNumero';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, CheckSquare, ChevronRight, Circle, Square, Zap } from 'lucide-react';
import { leer as leerLocal, escribir as escribirLocal } from '../../lib/almacenLocal';
import { MARGEN } from '../../lib/exceso';
import { num1, num0, alDecima } from '../../lib/numeros';
import { leerMacro, claseDelMacro, fondoDelMacro, llevaPunto } from '../../lib/estadoDelMacro';
import { loQueFaltaAlDia } from '../../lib/estadoDelDia';
import { suplementosPorComida } from '../../lib/suplementosDelDia';
import { tipoPorDefecto } from '../../lib/tipoDelDia';
import ExtrasDelDia from '../nutrition/ExtrasDelDia';

/**
 * «TU DIETA HOY» (doc de Jesús del 21-08, tarea 4.2; repintado con el del 23-08 y cerrado
 * con el artifact del 25-08, puntos 75 a 101): el deslizador de cuatro posiciones sobre los
 * tres números por macro -- el nombre con su punto encima, el número en blanco, la palabra
 * del estado debajo y la barra -- y, debajo, «Marca lo que te vayas comiendo» con las hechas
 * contraídas y una fila por toma, el intra y el post incluidos.
 *
 *  - Macros  · el objetivo del día CON el perientreno dentro. Es el mismo número que la
 *              cabecera de Nutrición: `resumen.P_total/H_total` del reparto vivo
 *              (`POST /calculator/distribute`) llevan el peri sumado, y `G_total` no lo
 *              lleva porque en el método la grasa del peri no cuenta. Aquí vive el
 *              interruptor que lo separa, y solo aquí.
 *  - Dieta   · la suma de lo montado: `servido_comidas` (lo cuenta el servidor, calibrado)
 *              más lo montado en el peri (P/H; la grasa del peri va fuera, como arriba).
 *  - Llevas  · la suma de lo MARCADO con su casilla, comidas y peri, MÁS los Extras del día
 *              que traen macros (Francisco, 5-09; revierte el punto 28 del 24-08): ver abajo.
 *  - Falta   · Macros menos Llevas: lo que te queda POR COMER hoy. PUEDE quedar en negativo
 *              y se dice tal cual («te pasas 14»), en el naranja de pasarse y sin bronca:
 *              es un dato. Y a la décima, como las otras tres (Francisco, 3-09).
 *
 * QUÉ COLOR LLEVA CADA COSA NO SE DECIDE AQUÍ: la regla entera está en
 * lib/estadoDelMacro, y las cuatro pestañas le preguntan lo mismo.
 *
 * LOS EXTRAS SUMAN EN LLEVAS (Francisco, 5-09). Del 24-08 al 5-09 estuvieron fuera: se
 * quitaron porque encogían el Falta (si se comía una tarta a media tarde, la app le decía
 * «ya no te comas la comida 4», que es enseñarle a compensar). El repaso del 4-09 (punto 09)
 * pidió que Llevas los sumara y Francisco lo decidió así. Solo suman los que traen macros
 * (los del catálogo); el texto a mano no tiene macros. Siguen en su lista, aparte
 * (nutrition/ExtrasDelDia), y Dieta no los cuenta: son lo comido FUERA de la dieta.
 *
 * LA CASILLA POR COMIDA vive en el servidor: `PATCH /diets/{fecha}/comida-marcada`
 * escribe `comidas.{k}.marcada` dentro del día, así la marca viaja con la cuenta a
 * cualquier aparato. El navegador (`almacenLocal`, por cliente y fecha) queda de red
 * por si la petición no llega; al recargar, lo del servidor manda sobre lo local.
 * Antes del 21-08 esta marca no existía en ninguna parte: los estados «Sin hacer /
 * Cuadrada» se calculan de los macros y nadie guardaba «me la he comido».
 */

const NOMBRE = { P: 'Proteína', H: 'Hidratos', G: 'Grasa' };
const NOMBRE_LLANO = { P: 'proteína', H: 'hidratos', G: 'grasa' };

const VISTAS = [
    { id: 'macros', label: 'Macros' },
    { id: 'dieta', label: 'Dieta' },
    { id: 'llevas', label: 'Llevas' },
    { id: 'falta', label: 'Falta' },
];

const PIE_DE_VISTA = {
    // El texto del punto 2 del doc del 23-08, palabra por palabra.
    macros: 'Los macros totales a los que tienes que llegar hoy. Debajo verás el desglose por comidas.',
    // Con «TU» (punto 91 del 25-08). Es su calculadora, no «la» calculadora: la diferencia
    // entre una herramienta que le han dado y una que es suya.
    dieta: 'Lo que tienes creado en tu calculadora',
    // «POR COMER», NO «PARA CUADRAR» (fichas 10 y 12 del repaso del 4-09). El número de esta
    // pestaña es Macros menos Llevas, o sea lo que queda por comerse hoy; «cuadrar» es lo que
    // se hace con la dieta en Nutrición, y aquí la frase hablaba de una cosa y el número de otra.
    falta: 'Lo que te queda por comer hoy',
};

// EL CONTADOR DE «LLEVAS», CON EL PERI APARTE (punto 93). El perientreno no es una comida y
// no se suma al número de comidas: son sus palabras. Se dice al lado, que es donde no
// estorba y donde se entiende sin explicarlo.
const contarLoMarcado = (comidas, peris) => {
    const deComidas = comidas === 0 ? '' : (comidas === 1 ? '1 comida marcada' : `${comidas} comidas marcadas`);
    const losDos = peris.length === 2;
    const dePeri = peris.length === 0 ? ''
        : (losDos ? 'intra y post' : `el ${peris[0].toLowerCase()}`);
    if (!dePeri) return deComidas;
    // Sin ninguna comida marcada el peri se dice solo, y en mayúscula: «El intra».
    if (!deComidas) return dePeri.charAt(0).toUpperCase() + dePeri.slice(1);
    return losDos ? `${deComidas}, ${dePeri}` : `${deComidas} y ${dePeri}`;
};

// LO QUE SUMA UNA COMIDA, Y LO DICE EL SERVIDOR (punto 3 del 17-08, rematado el 3-09).
//
// `servido_por_comida` viene calibrado en `GET /diets/{fecha}`, y el total del día es la
// suma de esos mismos números: por construcción, la fila no puede discrepar de arriba.
//
// El respaldo es la suma de `macros_efectivos` de lo guardado, que es lo que se hacía antes.
// Se queda por si la calibración no llega, pero NO es la buena: ese campo a veces no está en
// filas antiguas, y cuando está no siempre coincide con la calibración. Con todo en enteros
// no se notaba; con el decimal puesto, la misma comida decía «73,2H» abajo y «73,1» arriba.
const montadoDe = (comida) => (comida?.alimentos || []).reduce((acc, a) => {
    const m = a.macros_efectivos || {};
    return { P: acc.P + (m.P || 0), H: acc.H + (m.H || 0), G: acc.G + (m.G || 0) };
}, { P: 0, H: 0, G: 0 });

const servidoDe = (dieta, k) => {
    const s = dieta?.servido_por_comida?.[k];
    if (s) return { P: s.P || 0, H: s.H || 0, G: s.G || 0 };
    return montadoDe(dieta?.comidas?.[k]);
};

// SIN LETRA, EN EL ORDEN DE SIEMPRE: proteína · hidratos · grasa (Jesús, reunión del 3-09,
// ficha 26 del repaso del 4-09). Es la tercera vuelta de esto y conviene tenerlas todas:
//   punto 98 (25-08)   sin letra, porque el orden ya está en los rótulos de arriba
//   punto 173 (27-08)  con letra, «en el resto de la app llevan letra; aquí no»
//   reunión del 3-09   sin letra otra vez, y esta vez en toda la app (la fila de la comida
//                      en Nutrición la lleva MealCard, con la misma regla)
// Así que «32 · 19 · 6»; en el intra y el post, que no llevan grasa, «9 · 15».
//
// Y CON DECIMAL, LO MONTADO Y EL OBJETIVO (Francisco, 3-09-2026: «que muestre también en el
// global, así no hay desfase, tal cual lo hace Calma»). Esto REVIERTE el punto 80 -- «ni un
// decimal en Inicio, ni arriba ni en las comidas» -- y su excepción del 29, que dejaba pasar
// el medio gramo sólo en el objetivo.
//
// El motivo es el desfase: con la fila de Inicio en entero y la comida a la décima, el mismo
// plato salía «16G» aquí, «15,7» abierto y «válido +1» plegado. Tres cifras del mismo gramo.
// Y el medio gramo del objetivo (punto 115) entró para que los seis objetivos sumaran el día;
// a la décima suman igual de bien y además cuadran con lo que se lee al lado.
// LAS LETRAS VUELVEN (Francisco, 8-09-2026): «130P · 60H · 50G». Se quitaron entre el 3 y el
// 5 de septiembre por el parte de Gonzalo, con el argumento de que el orden ya dice qué es
// cada número; vuelven todas, y en la forma que nunca se llegó a perder en Extras y en las
// favoritas de comida: la letra pegada al número, sin espacio.
const lineaMacros = (m, sinGrasa = false, n = num1) => [
    `${n(m.P || 0)}P`, `${n(m.H || 0)}H`, ...(sinGrasa ? [] : [`${n(m.G || 0)}G`]),
].join(' · ');

// LA FILA DE CADA COMIDA: CON SUS LETRAS Y REDONDA (Francisco, 16-09): «49P · 22H · 10G».
//
// Las letras se fueron esta misma mañana -- «49 · 22 · 10», por su encargo de dejarlos «como
// números redondos» -- y vuelven esta misma tarde: «en el Inicio vuelven las letras de los
// macros». Manda lo último, y además es lo que ya decidió el 8-09 para toda la app: la letra
// pegada al número, sin espacio, en Inicio, en Extras y en las favoritas.
//
// Lo que SÍ se queda de aquel encargo es el redondeo, que es otra cosa y la pidió aparte: la
// lista dice «49P», no «49,1P». El gramo sigue entero donde se afina, dentro de la comida.
const lineaMacrosDeLaFila = (m, sinGrasa = false) => lineaMacros(m, sinGrasa, num0);

//: El objetivo va con el mismo `num1`, que no escribe «48,0»: los enteros salen enteros.
const lineaObjetivo = (m, sinGrasa = false) => lineaMacros(m, sinGrasa);

const nombreComida = (k, unica) => (unica ? 'Comida única' : `Comida ${k.slice(1)}`);

// El intra y el post, en el orden en que se toman: uno detrás del otro (punto 97).
const ORDEN_PERI = ['Intra', 'Post'];

/** «Las cuatro comidas», con el número escrito. El mismo que usa la banda de Nutrición. */
const NUMERO_EN_LETRA = { 1: 'La comida', 2: 'Las dos comidas', 3: 'Las tres comidas',
                          4: 'Las cuatro comidas', 5: 'Las cinco comidas', 6: 'Las seis comidas' };
const cuantasComidas = (n) => NUMERO_EN_LETRA[Number(n)] || 'Las comidas';

const TuDietaHoy = ({ api, userId, fecha, dieta, objetivo, servido, navigate, suplementos, provisionales }) => {
    // CON QUÉ PESTAÑA ABRE INICIO (ficha 11 del documento del 5-09, cerrada ese mismo día).
    // Esto ha cambiado cuatro veces y conviene tenerlas todas antes de tocarlo otra vez:
    //
    //   Macros  ->  el punto 170 del 27-08
    //   Llevas  ->  «Todo lo validado antes del 1 de septiembre», 1.1, con su motivo: «Macros
    //               es el objetivo y no cambia en todo el día; Llevas es por dónde va hoy, que
    //               es lo que ha venido a mirar»
    //   Dieta   ->  Francisco, 2-09
    //   3-09    ->  «Llevas si hay al menos una comida guardada; si no, Dieta»
    //   5-09    ->  la regla de ahora, en tres casos:
    //
    //       Falta alguna comida por crear ....... Dieta
    //       Todo creado, y hay algo marcado ..... Llevas
    //       Todo creado, y nada marcado ......... Dieta
    //
    // Lo que se cae es «al menos una comida guardada»: con una sola comida montada de
    // cuatro, Inicio abría en Llevas y ahí «le mide contra un total que todavía no es el
    // suyo» (ficha 11). Mientras la dieta esté sin terminar lo que toca es terminarla, y
    // para eso está Dieta; con el día ya creado, lo que se viene a mirar es por dónde vas.
    //
    // «CREADA» ES LO MISMO QUE DICE EL AVISO DE ARRIBA: ver `dietaSinTerminar`. Las dos
    // cosas se leen juntas en la misma pantalla, así que no pueden salir de dos cuentas.
    //
    // Sin nada marcado, Llevas no sacaba tres ceros pelados sino «Todavía no has marcado
    // nada»; eso sigue estando y funciona igual cuando se entra a esa pestaña.
    //
    // `vistaFijada` guarda que el cliente ya ha tocado las pestañas: a partir de ahí manda
    // él y la regla no vuelve a moverle la vista debajo de los dedos cuando llegue el día.
    const [vista, setVista] = useState('dieta');

    /* LA TARJETA NO ENCOGE AL CAMBIAR DE PESTAÑA (Jesús, 18-09). «Hay alguna caja que al ir
       de una pestaña a otra varía de altura y hace que el contenido de abajo suba y baje, eso
       hace parecer que el contenido depende de esa navegación por tabs.»

       Y tiene razón en el diagnóstico: las cuatro pestañas traen cosas distintas -- Macros
       lleva además el interruptor del perientreno, y «Llevas» sin nada marcado es una sola
       frase --, así que la lista de comidas de abajo daba un salto de casi cien píxeles cada
       vez que se tocaba una pestaña.

       SE MIDE, NO SE CLAVA UN NÚMERO. La altura buena depende del ancho, del texto y de si
       hay perientreno o no, así que un `min-h-[320px]` a ojo sobraría en un sitio y se
       quedaría corto en otro. Aquí se guarda la mayor altura que ha tenido el cuerpo y se usa
       de suelo: la tarjeta crece cuando hace falta y ya no baja. Al cambiar el ancho se
       vuelve a medir desde cero, que la altura de antes ya no vale. */
    const cuerpoRef = useRef(null);
    const [sueloDelCuerpo, setSueloDelCuerpo] = useState(0);
    const anchoMedido = useRef(typeof window === 'undefined' ? 0 : window.innerWidth);

    useEffect(() => {
        const medir = () => {
            const el = cuerpoRef.current;
            if (!el) return;
            const alto = el.scrollHeight;
            setSueloDelCuerpo((antes) => (alto > antes ? alto : antes));
        };
        medir();
        const alCambiarElAncho = () => {
            if (window.innerWidth === anchoMedido.current) return;   // el móvil dispara resize al abrir el teclado
            anchoMedido.current = window.innerWidth;
            setSueloDelCuerpo(0);
        };
        window.addEventListener('resize', alCambiarElAncho);
        return () => window.removeEventListener('resize', alCambiarElAncho);
    });
    const vistaFijada = useRef(false);
    // Y se decide UNA vez por día: guarda para qué fecha ya se decidió (ver el efecto de
    // más abajo, que espera al reparto del día para saber si la dieta está terminada).
    const vistaDecidida = useRef(null);
    // Las comidas ya marcadas se CONTRAEN («2 hechas · ocultas — Ver», punto 1 del doc
    // del 23-08): la lista enseña solo lo que queda por comer, y el «Ver» las despliega.
    const [verHechas, setVerHechas] = useState(false);
    // El peri va DENTRO del total por defecto (punto 87): el 250 ya lleva los 40 del peri.
    // Desmarcarlo es una forma de mirar la cuenta, no un ajuste que se guarde, así que
    // vuelve a su sitio al cambiar de día.
    const [periDentro, setPeriDentro] = useState(true);
    useEffect(() => { setVerHechas(false); setPeriDentro(true); }, [fecha]);
    // El reparto vivo del día: los totales con el peri y el objetivo de cada comida.
    const [reparto, setReparto] = useState(null);
    // SI YA SE SABE O TODAVÍA NO (Francisco, 16-09: «voy, termino el día, queda todo
    // cuadrado, vuelvo a Inicio y ese cartel antiguo tarda un milisegundo en cambiar, pero
    // se nota el salto y queda mal»).
    //
    // No era un cartel viejo guardado: era el de AHORA mismo, calculado sin el reparto. Los
    // totales del día llevan el perientreno dentro y el objetivo que llega con la dieta no,
    // así que durante ese primer instante el día se comparaba contra un objetivo más
    // pequeño, salía pasado y la banda decía «Te falta terminar el día» hasta que llegaba el
    // reparto. Un día cuadrado no puede anunciarse sin cuadrar ni medio segundo.
    //
    // Es la misma espera que ya hacía la pestaña con la que abre Inicio, por lo mismo y con
    // la misma frase: «la pantalla abría en una pestaña y saltaba a otra medio segundo
    // después con el cliente mirando». Se marca al llegar Y AL FALLAR: si el reparto no
    // llegara nunca, la banda tiene que salir igual, con el objetivo sin peri, que es lo
    // que hay.
    const [repartoResuelto, setRepartoResuelto] = useState(false);
    useEffect(() => { setRepartoResuelto(false); }, [fecha]);
    const [marcadas, setMarcadas] = useState({});
    // Los Extras del día: lo comido fuera de la dieta. Llegan con el documento del día
    // (`extras`) y el estado se queda aquí, en el padre, para que la lista sobreviva a un
    // repintado del bloque; añadir o quitar avisa hacia arriba. Los que traen macros suman en
    // Llevas (5-09), por eso el estado vive aquí y no dentro de la lista.
    const [extrasDia, setExtrasDia] = useState([]);
    useEffect(() => { setExtrasDia((dieta?.exists && dieta.extras) || []); }, [dieta]);

    const comidasGuardadas = (dieta?.exists && dieta.comidas) || {};

    // Al cambiar de día se vuelve a decidir sola: la elección era para el día que estaba
    // mirando, no para siempre. (La regla de los tres casos vive abajo, con `dietaSinTerminar`.)
    useEffect(() => { vistaFijada.current = false; }, [fecha]);

    const elegirVista = (cual) => { vistaFijada.current = true; setVista(cual); };

    // La marca de cada comida: manda el servidor (`comidas.{k}.marcada`, la escribe
    // PATCH /diets/{fecha}/comida-marcada) y el navegador queda de red por si la
    // peticion no llega: la marca es del cliente en cualquier aparato, no de un
    // navegador (bloque 4 del doc 21-08).
    useEffect(() => {
        let delNavegador = {};
        try { delNavegador = JSON.parse(leerLocal(`inicio-marcadas-${fecha}`, userId) || '{}') || {}; }
        catch { delNavegador = {}; }
        const delServidor = Object.fromEntries(
            Object.entries(comidasGuardadas).filter(([, c]) => c?.marcada === true).map(([k]) => [k, true]));
        setMarcadas({ ...delNavegador, ...delServidor });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fecha, userId, dieta]);

    const marcar = (k) => {
        setMarcadas((prev) => {
            const sig = { ...prev, [k]: !prev[k] };
            escribirLocal(`inicio-marcadas-${fecha}`, userId, JSON.stringify(sig));
            api.patch(`/diets/${fecha}/comida-marcada`, { comida: k, marcada: !prev[k] })
                .catch((e) => console.error('No se pudo guardar la marca de la comida', e));
            return sig;
        });
    };

    // EL DOCUMENTO DEL DÍA PUEDE EXISTIR SIN DIETA MONTADA. Desde el 24-08 apuntar un extra
    // es un campo de texto y un toque, y ese POST hace upsert del día: quien apunta el café
    // de las 10:00 antes de montar nada ya tiene documento, con `exists: true` y sin
    // configuración dentro. Si eso se diera por «día configurado», a quien tiene 3 comidas
    // le pintaríamos 4 vacías. El marcador bueno es `num_comidas`, que lo escribe siempre
    // `upsert_diet_doc` en cuanto se guarda una dieta de verdad.
    const diaConfigurado = Boolean(dieta?.exists && dieta.num_comidas);

    // El reparto se pide UNA vez con la configuración del día: la de la dieta si está
    // guardada y, si no, la que el cliente tiene puesta (`/user/diet-config`), que es la
    // misma precedencia que aplica el servidor al resolver `objetivo_comidas`.
    useEffect(() => {
        let cancelado = false;
        const cargar = async () => {
            try {
                let cfg;
                if (diaConfigurado) {
                    cfg = {
                        tipo_dia: dieta.tipo_dia || 'entrenamiento',
                        num_comidas: dieta.num_comidas || 4,
                        momento_entreno: dieta.momento_entreno != null ? dieta.momento_entreno : 1,
                        opcion_peri: dieta.opcion_peri || 'intra_post',
                    };
                } else {
                    const r = await api.get('/user/diet-config').catch(() => ({ data: {} }));
                    cfg = {
                        // EL MISMO TIPO QUE NUTRICIÓN (ficha 61 del repaso del 4-09). Aquí
                        // ponía «entrenamiento» a pelo y un sábado vacío salía repartido con
                        // intra y post mientras Nutrición lo abría en descanso. El tipo con el
                        // que abre el día lo resuelve ya el servidor (`tipo_dia_previsto` en
                        // GET /diets/{fecha}: la rutina si la hay, si no el finde en descanso);
                        // si no llegara, la misma regla de la pantalla (`lib/tipoDelDia`).
                        tipo_dia: dieta?.tipo_dia_previsto || tipoPorDefecto(fecha),
                        num_comidas: r.data?.num_comidas || 4,
                        momento_entreno: r.data?.momento_entreno != null ? r.data.momento_entreno : 1,
                        opcion_peri: r.data?.opcion_peri || 'intra_post',
                    };
                }
                const res = await api.post('/calculator/distribute', {
                    fecha, ...cfg, single_meal: cfg.num_comidas === 1,
                });
                if (!cancelado) { setReparto(res.data); setRepartoResuelto(true); }
            } catch (err) {
                if (!cancelado) setRepartoResuelto(true);
                // Sin reparto no se rompe nada: los totales caen al objetivo sin peri, que
                // ya viene resuelto con la dieta. El porqué, a la consola.
                console.error('[inicio] no se pudo traer el reparto del día', err);
            }
        };
        cargar();
        return () => { cancelado = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, fecha, dieta]);

    // ── Las cuatro cuentas ──
    // Macros: con el peri dentro (resumen del reparto). Mientras el reparto no llega, el
    // objetivo sin peri, que es lo que hay: un número de menos, nunca uno inventado.
    const conPeri = reparto?.resumen
        ? { P: reparto.resumen.P_total || 0, H: reparto.resumen.H_total || 0, G: reparto.resumen.G_total || 0 }
        : objetivo;

    // Y SOLO SI EL DÍA LO LLEVA (10-09). Esto sumaba el Intra y el Post estuviera el día
    // configurado con perientreno o no, y esas dos comidas no se borran al quitarlo: se
    // guardan por si vuelve. Así que a quien se lo quitaba, Inicio le seguía sumando un
    // peri que su pantalla ya no enseña. Es el mismo fallo que la comida escondida, en su
    // versión de peri; el servidor lo arregla en `core/comidas_del_dia`.
    const periMontado = useMemo(() => {
        const llevaPeri = dieta?.tipo_dia !== 'descanso'
            && ['intra_post', 'solo_intra', 'solo_post'].includes(dieta?.opcion_peri);
        if (!llevaPeri) return { P: 0, H: 0 };
        const intra = dieta?.opcion_peri === 'solo_post' ? { P: 0, H: 0 } : servidoDe(dieta, 'Intra');
        const post = dieta?.opcion_peri === 'solo_intra' ? { P: 0, H: 0 } : servidoDe(dieta, 'Post');
        return { P: intra.P + post.P, H: intra.H + post.H };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dieta]);

    // Dieta: EL TOTAL LO DA EL SERVIDOR (punto 4 de Jesús, 8-09: «Inicio y Nutrición no
    // suman igual la misma dieta»).
    //
    // Aquí se sumaba a mano: las comidas regulares del servidor más el peri calculado en el
    // navegador. Y esa cuenta rehace una regla que el servidor ya sabe -- qué comidas tiene
    // el día, si el peri va o no, y cuál de los dos --, así que en cuanto una de las dos
    // cambiaba, las pantallas discrepaban. Medido el 10-09 sobre un día real: Inicio recibía
    // 140,0 P y Nutrición 165,0 P, y los 25 de diferencia eran justo el perientreno.
    //
    // Ahora el día trae `macros` hecho, que es el mismo `_total_como_arriba` que reciben
    // Nutrición, Mi semana y el calendario. Su regla, para que no se pierda al leerlo: la
    // proteína y los hidratos llevan el peri dentro y la grasa no, porque en el método el
    // peri no gasta grasa del día.
    //
    // El respaldo de abajo es lo de antes, y sólo entra si el día viene de una respuesta sin
    // `macros` (una guardada de antes de este cambio). Va anotado a propósito: un respaldo
    // que cuenta distinto es exactamente lo que causó este punto.
    const totalDieta = dieta?.macros ? {
        P: dieta.macros.P || 0, H: dieta.macros.H || 0, G: dieta.macros.G || 0,
    } : {
        P: (servido?.P || 0) + periMontado.P,
        H: (servido?.H || 0) + periMontado.H,
        G: servido?.G || 0,
    };

    // Qué comidas tiene el día, en su orden. De lo guardado si el día lleva su
    // configuración dentro; si no, del reparto (que salió de la del cliente).
    const numComidas = diaConfigurado ? dieta.num_comidas
        : (reparto?.comidas ? Object.keys(reparto.comidas).length : 4);
    const esUnica = numComidas === 1;
    const claves = ['C1', 'C2', 'C3', 'C4'].slice(0, Math.max(1, Math.min(4, numComidas)));

    // Los suplementos que le tocan con cada comida (punto 174). Ver `suplementosPorComida`.
    const supPorComida = useMemo(() => suplementosPorComida(suplementos, claves),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [suplementos, claves.join(',')]);

    const montadoPorComida = useMemo(() => Object.fromEntries(
        claves.map((k) => [k, servidoDe(dieta, k)])),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dieta, numComidas]);

    // EL INTRA Y EL POST, CADA UNO EL SUYO (puntos 96 y 97 del 25-08). Hasta hoy se fundían
    // en una sola tarjeta llamada «Perientreno», sin casilla, y por eso Llevas no llegaba
    // NUNCA al total: marcabas las cuatro comidas, te tomabas el batido y te seguían
    // faltando los 40 de proteína. Son dos tomas con nombre propio, como la Comida 1 y la 2.
    //
    // Lo que suma al marcarlo es lo que la fila enseña: lo montado si lo hay y, si no, su
    // objetivo. En las comidas eso no vale (lo que te comes depende de lo que montes), pero
    // el peri es una toma fija -- el batido de siempre --, así que el número se sabe sin
    // montarlo, y es el que hay que poder marcar para que la cuenta cierre.
    const periPorBloque = useMemo(() => {
        const objetivos = reparto?.periworkout || {};
        const res = {};
        for (const k of ORDEN_PERI) {
            const guardada = comidasGuardadas[k];
            const tiene = (guardada?.alimentos || []).length > 0;
            const obj = objetivos[k];
            if (!tiene && !obj) continue;
            const montado = servidoDe(dieta, k);
            res[k] = {
                tiene,
                montado,
                objetivo: obj ? { P: obj.P || 0, H: obj.H || 0, G: 0 } : null,
                // La grasa del peri no cuenta en el método, así que ni se pinta ni se suma.
                suma: tiene ? { ...montado, G: 0 } : { P: obj.P || 0, H: obj.H || 0, G: 0 },
            };
        }
        return res;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dieta, reparto]);

    const clavesPeri = ORDEN_PERI.filter((k) => periPorBloque[k]);

    // El sitio del peri en la lista: justo después de la comida tras la que se entrena
    // (`momento_entreno`, 0 en ayunas, 1 después de la C1...). Y los dos juntos, que se
    // toman uno detrás de otro. Si el día tiene menos comidas que el momento apuntado, van
    // al final: nunca desaparecen de la lista por un dato viejo.
    const momentoEntreno = diaConfigurado
        ? (dieta.momento_entreno != null ? dieta.momento_entreno : 1)
        : (reparto?.config?.momento_entreno != null ? reparto.config.momento_entreno : 1);
    const trasComida = Math.max(0, Math.min(claves.length, Number(momentoEntreno) || 0));

    const filas = useMemo(() => {
        const lista = [];
        const meterPeri = () => clavesPeri.forEach((k) => lista.push({ clave: k, esPeri: true }));
        if (trasComida === 0) meterPeri();
        claves.forEach((k, i) => {
            lista.push({ clave: k, esPeri: false });
            if (i + 1 === trasComida) meterPeri();
        });
        return lista;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [claves.join(','), clavesPeri.join(','), trasComida]);

    const hechas = filas.filter((f) => marcadas[f.clave]);

    // LA QUE TOCA AHORA (bloque 07 del doc «Cómo abre Inicio», 3-09).
    //
    // Su documento dice: «el Post lleva una barra naranja a la izquierda que no dice qué
    // significa» y «la marca del Post pasa a ser una palabra, Ahora, que se entiende sin
    // leyenda». O sea que NO es una marca nueva en cualquier fila: es LA MARCA QUE YA HAY
    // -- la del peri -- convertida en palabra cuando a esa toma le toca.
    //
    // Y «le toca» es secuencial (Francisco, 3-09): la primera SIN MARCAR en el orden del
    // día, que ya lo monta `filas` con el intra y el post en su sitio según el momento de
    // entreno. No hay reloj de por medio: se lee de lo que él ha ido marcando.
    //
    // Así que el chip sale en el intra o en el post cuando son los siguientes, y entonces
    // sustituyen su barra. Las demás filas no llevaban marca y siguen sin llevarla.
    //
    // Con el día entero marcado no toca ninguna, que es lo correcto.
    const siguienteSinMarcar = filas.find((f) => !marcadas[f.clave]) || null;
    const laDeAhora = siguienteSinMarcar?.esPeri ? siguienteSinMarcar.clave : null;
    // «LLEVAS» SUMA LAS COMIDAS MARCADAS Y LOS EXTRAS (Francisco, 5-09: «Llevas sume los
    // extras»). REVIERTE el punto 28 del doc del 24-08, que los dejaba fuera para que un
    // extra no encogiera el «Falta» y la app no le enseñara a compensar. El punto 09 del
    // repaso del 4-09 pedía lo contrario («lleva 80 de hidratos apuntados y Falta sigue
    // pidiendo 120»), y Francisco eligió sumarlos. Cuentan los extras que traen macros, los
    // del catálogo; los escritos a mano no tienen macros y siguen sin contar, como dice el
    // propio bloque de Extras.
    const llevasDeLasComidas = hechas.reduce((acc, f) => {
        const m = f.esPeri ? periPorBloque[f.clave].suma : montadoPorComida[f.clave];
        return { P: acc.P + m.P, H: acc.H + m.H, G: acc.G + (m.G || 0) };
    }, { P: 0, H: 0, G: 0 });
    const llevasDeLosExtras = (extrasDia || []).reduce((acc, e) => {
        const m = e?.macros;
        if (!m) return acc;
        return { P: acc.P + (m.P || 0), H: acc.H + (m.H || 0), G: acc.G + (m.G || 0) };
    }, { P: 0, H: 0, G: 0 });
    const hayExtrasConMacros = llevasDeLosExtras.P + llevasDeLosExtras.H + llevasDeLosExtras.G > 0;
    const llevas = {
        P: llevasDeLasComidas.P + llevasDeLosExtras.P,
        H: llevasDeLasComidas.H + llevasDeLosExtras.H,
        G: llevasDeLasComidas.G + llevasDeLosExtras.G,
    };

    // Lo que falta, A LA DÉCIMA como todo lo demás (ficha 12 del repaso del 4-09). Iba a
    // `Math.round` desde el punto 80, y era la única pestaña que se quedó en entero cuando el
    // 3-09 toda la app pasó a la décima (51dec7e): con la grasa a 84,5 sobre 80 el número
    // grande decía «4» y la palabra de debajo y la frase de aviso «te pasas 4,5». El desvío
    // exacto se guarda aparte para la frase, que es la misma cifra ya sin redondear.
    const faltaExacto = conPeri ? {
        P: conPeri.P - llevas.P, H: conPeri.H - llevas.H, G: conPeri.G - llevas.G,
    } : null;
    const falta = faltaExacto ? {
        P: alDecima(faltaExacto.P), H: alDecima(faltaExacto.H), G: alDecima(faltaExacto.G),
    } : null;
    // (La línea «Te has pasado X g de…» de debajo de los números se quitó el 25-09: la celda
    // ya lo dice. Con ella se fue `pasadas`, que solo servía para eso.)

    // EL PERIENTRENO, DENTRO O APARTE (puntos 86 a 88). Los dos números salen del MISMO
    // reparto, así que la resta cuadra siempre: el total lleva el peri sumado
    // (`resumen.P_total`) y quitárselo da exactamente el objetivo de las comidas, que es el
    // número que enseña Mis macros. La grasa no se toca: en el método la del peri no cuenta,
    // y por eso `G_total` nunca la llevó.
    const periTotal = useMemo(() => ORDEN_PERI.reduce((acc, k) => {
        const o = (reparto?.periworkout || {})[k];
        return o ? { P: acc.P + (o.P || 0), H: acc.H + (o.H || 0) } : acc;
    }, { P: 0, H: 0 }), [reparto]);
    const hayPeriEnElDia = periTotal.P > 0 || periTotal.H > 0;
    const sinPeri = conPeri
        ? { P: conPeri.P - periTotal.P, H: conPeri.H - periTotal.H, G: conPeri.G }
        : conPeri;

    const valoresDeVista = {
        macros: periDentro ? conPeri : sinPeri,
        dieta: totalDieta, llevas, falta,
    };
    const valores = valoresDeVista[vista];
    // Con un extra con macros apuntado y ninguna comida marcada, Llevas ya no dice «Todavía
    // no has marcado nada»: enseña lo que suman los extras (5-09; antes, con los extras
    // fuera, el número habría sido un 0 pelado y por eso se tapaba).
    const nadaMarcado = hechas.length === 0 && !hayExtrasConMacros;

    // SI LA DIETA DEL DÍA NO LLEGA A SUS MACROS, QUE SE VEA (revisión del 2-09, y el aviso
    // entero con el doc «Cómo abre Inicio» del 3-09).
    //
    // EL MARGEN ES EL DE LA CASA. Aquí vivía un 5 a pelo mientras el resto de la app daba
    // un macro por bueno con 4 («de 1 a 4, falte o sobre, es válido», punto 11.1, plano en
    // todo desde el 3-09): con 4,5 de desvío el punto no salía y la comida sí decía que no
    // cuadraba. Ahora sale de `lib/exceso`, como en todas partes.
    //
    // Y SIN DÍA CREADO TAMBIÉN (ficha 18 del repaso del 4-09). Esto exigía `dieta.exists`, así
    // que el día sin documento -- el que más lejos está de sus macros, con todo por meter --
    // era el único que no sacaba ni el punto ni el aviso. El desvío de un día que no existe es
    // el objetivo entero: «Te faltan 300 gramos de proteína para terminar de crear tu dieta
    // del día», y «Terminarla» lleva a Nutrición a crearlo.
    const desvioDeLaDieta = conPeri
        ? { P: (totalDieta.P || 0) - (conPeri.P || 0),
            H: (totalDieta.H || 0) - (conPeri.H || 0),
            G: (totalDieta.G || 0) - (conPeri.G || 0) }
        : null;
    const fueraDeMargen = desvioDeLaDieta
        ? ['P', 'H', 'G'].filter((k) => Math.abs(desvioDeLaDieta[k]) > MARGEN) : [];
    const dietaNoCuadra = fueraDeMargen.length > 0;

    // «FALTA ALGUNA COMIDA POR CREAR» (ficha 11 del 5-09), que es lo que decide con qué
    // pestaña abre Inicio. Es LO MISMO que dice el aviso de arriba, y a propósito: mientras
    // la dieta del día no llegue a sus macros está sin terminar de crear -- eso es lo que le
    // dice el propio texto del aviso (ficha 10) --, así que las dos cosas no pueden salir de
    // dos cuentas distintas o la pantalla se contradice a sí misma.
    //
    // Y se mira ADEMÁS comida a comida, para el caso raro en que los totales del día cuadren
    // con una comida vacía porque otra lleva de más: ahí también falta una por crear, y es
    // literalmente lo que él dice.
    const hayComidaVacia = !diaConfigurado
        || claves.some((k) => (comidasGuardadas[k]?.alimentos || []).length === 0);
    const dietaSinTerminar = dietaNoCuadra || hayComidaVacia;

    // LA REGLA DE LOS TRES CASOS, APLICADA UNA VEZ POR DÍA (ficha 11 del 5-09; la tabla está
    // arriba, junto a `vista`).
    //
    // SE ESPERA AL REPARTO DEL DÍA: hasta que llega no se sabe cuál es el objetivo con el
    // perientreno dentro, y sin eso no se sabe si la dieta está terminada. Decidiendo antes,
    // la pantalla abría en una pestaña y saltaba a otra medio segundo después con el cliente
    // mirando. Mientras no llegue se queda en Dieta, que es el arranque y el caso seguro: si
    // el reparto no llegara nunca, quedarse en la pestaña que enseña lo creado no engaña a
    // nadie.
    //
    // Y sólo mientras el cliente no haya tocado las pestañas (`vistaFijada`): si ya eligió
    // una, moverle la vista debajo de los dedos cuando llegue un dato sería peor que
    // acertar la pestaña.
    useEffect(() => {
        if (vistaFijada.current || !reparto || vistaDecidida.current === fecha) return;
        vistaDecidida.current = fecha;
        setVista(dietaSinTerminar || nadaMarcado ? 'dieta' : 'llevas');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reparto, fecha, dietaSinTerminar, nadaMarcado]);

    // LO QUE LE PASA A LA DIETA, CON SUS PALABRAS (ficha 10 del 5-09): «Te faltan 18,6
    // gramos de proteína para terminar de crear tu dieta del día».
    //
    // Hasta hoy decía «Te faltan 18,6 g de proteína por meter», que es su maqueta del 3-09.
    // Cambian dos cosas: la frase dice QUÉ PASA SI NO LO HACE («para terminar de crear tu
    // dieta del día») y usa «crear», que es su palabra.
    //
    // Y LOS DICE TODOS, no sólo el primero: «te faltan 18,6 g de proteína y 9 g de hidratos
    // para terminar de crear tu dieta del día». En el teléfono ocupa dos líneas y está bien.
    //
    // «GRAMOS» ENTERO CON UNO SOLO, «g» EN LA LISTA: son sus dos frases, literales, y se
    // entiende por qué. Con un macro cabe la palabra entera; con dos o tres, repetirla no
    // dice nada nuevo y alarga el aviso justo cuando ya es más largo.
    //
    // Su documento sólo dibuja el caso de faltar, pero el aviso salta igual cuando el día
    // creado SE PASA, y ahí «te faltan» sería mentira: se dice cada cosa por su nombre y,
    // si se dan las dos, las dos. El final cambia, porque «para terminar de crear» no vale
    // para lo que sobra: eso no está sin crear, está de más.
    //
    // EL NÚMERO SALE DE LOS NÚMEROS QUE ÉL LEE, no del desvío exacto: el aviso no puede decir
    // una cifra distinta de la que tiene justo debajo, que es lo que viene a evitar. Se resta
    // lo pintado, y desde el 3-09 lo pintado va a la décima en toda la pantalla.
    const desvioQueSeVe = desvioDeLaDieta
        ? Object.fromEntries(['P', 'H', 'G'].map((k) =>
            [k, alDecima(totalDieta[k] || 0) - alDecima(conPeri[k] || 0)]))
        : null;
    // Una sola unidad para toda la frase: la palabra entera cuando sólo se nombra un macro.
    const unidad = fueraDeMargen.length === 1 ? 'gramos' : 'g';
    const enPalabras = (lista) => lista
        .map((k) => `${num0(Math.abs(desvioQueSeVe[k]))} ${unidad} de ${NOMBRE_LLANO[k]}`)
        .reduce((a, t, i, l) => (i === 0 ? t : i === l.length - 1 ? `${a} y ${t}` : `${a}, ${t}`), '');
    const loQueFalta = fueraDeMargen.filter((k) => desvioDeLaDieta[k] < 0);
    const loQueSobra = fueraDeMargen.filter((k) => desvioDeLaDieta[k] > 0);
    // UN DÍA VACÍO NO ESTÁ A MEDIAS (Jesús, 11-09, punto 14).
    //
    // Con la dieta A CERO, sin una sola comida, esto decía «te faltan 13,2 g de proteína...
    // para TERMINAR de crear tu dieta del día», como si le quedara poco. Y Nutrición, al
    // lado, decía del mismo día «Día sin crear». Dos pantallas contando lo mismo de dos
    // formas.
    //
    // Su arreglo: dos frases, no una. Si no hay nada, «Todavía no has creado la dieta de
    // hoy»; si hay comidas pero falta, «Te falta terminar el día», que es lo que ya dice
    // Nutrición.
    const diaSinCrear = !Object.entries(dieta?.comidas || {})
        .some(([, c]) => ((c || {}).alimentos || []).length > 0);

    // LA BANDA DE INICIO, LA MISMA QUE NUTRICIÓN (artefacto, 12-09).
    //
    // «La misma banda arriba», «tres estados y ninguno más: sin crear, sin terminar, listo»
    // y «el mismo estado en los cuatro sitios: Nutrición, la línea de Inicio, Mi semana y la
    // campana. Se calcula una vez y se escribe igual en todas partes. Hoy Inicio se inventa
    // el suyo».
    //
    // Se lo inventaba de verdad: una franja ROJA de una línea con «Terminarla» a la derecha,
    // que además salía igual el día que no había ni una comida creada. El rojo venía de la
    // ficha 07 del 5-09 -- «esto no es un estado de la dieta, es algo sin terminar» --, y esa
    // ficha queda atrás: manda lo más reciente, y lo más reciente es el artefacto, que pinta
    // esta banda en el naranja de la casa y con las mismas palabras que Nutrición.
    //
    // La segunda frase sí cambia de una pantalla a otra, y en el artefacto también: aquí se
    // habla del día de HOY («para cerrar el día de hoy»), mientras Nutrición habla del día
    // que tengas delante.
    const estadoDeLaBanda = diaSinCrear ? 'sin_crear' : dietaNoCuadra ? 'sin_terminar' : 'listo';
    const comidasSinCrear = claves.filter((k) => (comidasGuardadas[k]?.alimentos || []).length === 0);
    const faltaDelDia = conPeri ? loQueFaltaAlDia(totalDieta, conPeri, num0) : null;
    const tituloDeLaBanda = { listo: '✓ Día listo', sin_terminar: '⚠ Te falta terminar el día',
                              // Sin el «○» (Jesús, 27-09 23:39: «quita ese círculo horrible
                              // que pone día sin crear; ese círculo sobra»).
                              sin_crear: 'Día sin crear' }[estadoDeLaBanda];
    // AQUÍ NO SE NOMBRAN LAS COMIDAS, PORQUE INICIO NO LAS MIDE (13-09). Su frase del día
    // listo es «las cuatro comidas y el total del día», y en Nutrición se puede decir porque
    // allí está el objetivo de cada comida; aquí solo llega el del día, así que prometer que
    // las cuatro están bien sería decir algo que esta pantalla no ha comprobado. Pasó de
    // verdad el sábado 12: día en verde y la comida 1 a 4,4 g de grasa.
    //
    // No rompe «el mismo estado en los cuatro sitios»: el estado es el mismo, «✓ Día listo».
    // Y la segunda frase ya cambia de una pantalla a otra en su propio artefacto, que en
    // Inicio dice «para cerrar el día de hoy» y en Nutrición no.
    const pieDeLaBanda = estadoDeLaBanda === 'listo'
        ? 'El total del día. No te falta nada.'
        : estadoDeLaBanda === 'sin_crear'
            ? 'Todavía no has creado ninguna comida de este día.'
            // Y si se pasa, se dice: es el caso que el artefacto no dibuja y la app sí
            // conoce, y callarlo dejaría la frase en blanco justo cuando hay algo que contar.
            : [faltaDelDia ? `${faltaDelDia} para cerrar el día de hoy.`
                : loQueSobra.length ? `Te pasas ${enPalabras(loQueSobra)} en tu dieta del día.` : null,
                // LO MISMO QUE NUTRICIÓN CUANDO FALTAN COMIDAS POR CREAR: las dos pantallas
                // hablan del mismo día, así que no pueden contar dos cosas distintas.
                comidasSinCrear.length === 1
                    ? `Todavía no has creado la Comida ${claves.indexOf(comidasSinCrear[0]) + 1}.`
                    : comidasSinCrear.length > 1
                        ? `Todavía te faltan ${comidasSinCrear.length} comidas por crear.` : null]
                .filter(Boolean).join(' ') || 'Todavía le falta algo al día.';

    // AQUÍ VIVÍA `avisoDeLaDieta`, la frase de la ficha 10 del 5-09 («Te faltan 18,6 gramos
    // de proteína para terminar de crear tu dieta del día»). Desde que entró la banda del
    // 12-09 con sus tres estados, esa frase la escribe `pieDeLaBanda` unas líneas más abajo
    // y esto se seguía calculando sin que lo leyera nadie. Un texto que no se pinta y
    // sigue vivo es el que un día vuelve a la pantalla por un descuido.

    // EL AVISO YA NO SE CIERRA A MANO (ficha 08 del 5-09: «se quita la ×»).
    //
    // Aquí vivía la «×» del bloque 03 del 3-09 y lo que guardaba su cierre por ese día
    // (`inicio-aviso-dieta-{fecha}` en el navegador). Se va entera, con su motivo: «eso no
    // lo ha pedido nadie y tiene consecuencia: si se cierra, y en el móvil se cierra sin
    // querer, el cliente se queda el día sin dieta y sin nada que se lo recuerde, porque el
    // aviso es la única vía». El aviso desaparece SOLO cuando la dieta está terminada, y
    // ésa es su única salida.

    // «TERMINARLA», NO «VERLO» (bloque 03 del 3-09): «Verlo te enseña el problema;
    // Terminarla te lleva a arreglarlo».
    //
    // Y LLEVA A LA COMIDA QUE FALTA (ficha 09 del 5-09): «yo daría a Terminar y que me lleve
    // directamente a la comida que falta». Es la PRIMERA a la que le falten macros EN EL
    // ORDEN DEL DÍA -- con el intra y el post dentro, que también son tomas que se montan --,
    // no la primera de la lista de comidas. Antes se miraban sólo C1..C4 y en su orden fijo.
    //
    // Si son varias no hay que inventar nada: al terminar esa, el aviso se recalcula solo y
    // el botón lleva a la siguiente.
    //
    // Si ninguna se queda corta pero el día no cuadra (el caso de pasarse), se señala la
    // primera que se pasa, que es donde está lo que hay que quitar. Y si no se puede señalar
    // ninguna -- el día sin crear, o sin reparto todavía --, se va a Nutrición sin más, que
    // abre en «Todavía sin crear». Arreglar una dieta se hace allí: en Inicio las filas son
    // casillas para marcar lo comido, no la cocina.
    const comidaDondeFalta = useMemo(() => {
        const cuentaDe = ({ clave, esPeri }) => {
            const objetivo = esPeri ? periPorBloque[clave]?.objetivo : reparto?.comidas?.[clave];
            const montado = esPeri ? periPorBloque[clave]?.montado : montadoPorComida[clave];
            if (!objetivo || !montado) return null;
            // La grasa del peri no cuenta en el método, así que tampoco puede faltar.
            return { objetivo, montado, macros: esPeri ? ['P', 'H'] : ['P', 'H', 'G'] };
        };
        const fueraPor = (f, signo) => {
            const c = cuentaDe(f);
            return !!c && c.macros.some((x) =>
                signo * ((c.montado[x] || 0) - (c.objetivo[x] || 0)) > MARGEN);
        };
        const corta = filas.find((f) => fueraPor(f, -1));
        if (corta) return corta.clave;
        // UNA COMIDA VACÍA SE SABE SIN OBJETIVOS, y el reparto del día tarda en llegar: quien
        // pulsaba el botón en el primer segundo aterrizaba en Nutrición sin ninguna comida
        // abierta, porque todavía no había con qué comparar. Vacía es la que más falta.
        const vacia = filas.find(({ clave }) => !(comidasGuardadas[clave]?.alimentos || []).length);
        if (vacia) return vacia.clave;
        return filas.find((f) => fueraPor(f, 1))?.clave || null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filas, reparto, montadoPorComida, periPorBloque, dieta]);

    // Abre la pestaña Dieta y esa comida (ficha 09). La pestaña es la que enseña lo creado y
    // la que lleva el punto rojo diciendo dónde; con la regla de la ficha 11 Inicio ya abre
    // ahí mientras la dieta esté sin terminar, y esto la deja puesta también para quien la
    // había cambiado a mano antes de tocar el botón.
    const terminarla = () => {
        setVista('dieta');
        navigate(diaConfigurado && comidaDondeFalta
            ? `/dashboard/nutrition?comida=${comidaDondeFalta}`
            : '/dashboard/nutrition');
    };

    // El contador de Llevas: las comidas por un lado y el peri por otro (punto 93).
    const contadorDeLlevas = contarLoMarcado(
        hechas.filter((f) => !f.esPeri).length,
        hechas.filter((f) => f.esPeri).map((f) => f.clave));

    // EL PIE DE FALTA CAMBIA CUANDO EL DÍA ESTÁ HECHO: los tres ceros en verde son el final
    // del día, y el pie lo dice en vez de seguir pidiendo lo que ya no queda.
    const diaCuadrado = Boolean(conPeri) && ['P', 'H', 'G'].every((k) => leerMacro({
        vista: 'falta', hay: alDecima(llevas[k]), objetivo: alDecima(conPeri[k]),
    }).color === 'ok');
    const pieDeFalta = diaCuadrado ? 'Día cuadrado. Mañana seguimos.' : PIE_DE_VISTA.falta;

    // CADA COMIDA ATERRIZA EN LA SUYA, no solo el peri.
    //
    // Esto nació en el P32 del 23-08 para el peri: «pinchar su tarjeta te dejaba en la cocina
    // con la Comida 1 delante, como si el peri no fuera contigo». Y se quedó ahí: las comidas
    // normales seguían yendo a Nutrición a secas, o sea a la Comida 1, dijeras la que dijeras.
    // Lo cazó Gonzalo probando en pantalla: «entran en el día, no entran en la comida».
    //
    // El destino ya sabía recibirlo (`?comida=C1..C4|Intra|Post` en NutritionPage); lo único
    // que faltaba era decirle cuál.
    const irALaComida = (clave) => navigate(`/dashboard/nutrition?comida=${clave}`);

    return (
        <>
            {/* EL AVISO DE LA DIETA SIN TERMINAR: FUERA DEL CUADRO Y ENCIMA (ficha 07 del
                documento del 5-09, aprobada por Francisco ese mismo día).

                DÓNDE VIVÍA Y POR QUÉ SE MUEVE. El 3-09 se metió DENTRO de la pestaña Dieta
                («Cómo abre Inicio», bloques 01 y 03) porque el 2-09, suelto encima de los
                números, chocaba: en Llevas decía «faltan 12» y justo debajo había un 107 que
                era otra cosa. Pero dentro de la pestaña sólo lo ve quien abre en Dieta, y a
                Jesús la app le abrió en Llevas: «si abre en la pestaña que no es, el aviso no
                existe para el cliente». Sus tres razones, literales: (1) un aviso que depende
                de la pestaña que mires no es un aviso; (2) dentro del cuadro empuja los tres
                números hacia abajo y parte la tarjeta en dos; (3) es lo que se dijo el 3:
                «ese mensaje es un aviso que se ve siempre arriba».
                El choque del 2-09 ya no se da: aquí arriba el aviso no está pegado a ningún
                número, está encima de la tarjeta entera y habla de la dieta del día.

                UNA FRANJA FINA DE ANCHO COMPLETO: icono, el texto en una línea y «Terminarla»
                a la derecha; en el teléfono el botón debajo, porque la frase con dos macros ya
                ocupa dos líneas y contra el botón se partiría en renglones de tres palabras.

                Y EL PUNTO ROJO DE LA PESTAÑA DIETA SE QUEDA (ficha 07): el aviso dice cuánto
                falta y el punto dice dónde.

                SIN «×» (ficha 08): la única salida del aviso es que la dieta esté terminada.

                El rojo es a propósito y está fuera de la regla de colores de la casa: verde
                cuadrado, naranja pasarse y sin color ir por debajo, y esto no es ninguna de
                las tres -- no es un estado de la dieta, es algo que está sin terminar. */}
            {/* MIENTRAS NO SE SEPA, LA BANDA NO DICE NADA (16-09). El día sin una sola
                comida sí se sabe sin reparto -- no hay nada que comparar --, así que ése sale
                de inmediato; los demás esperan a `repartoResuelto`. El hueco mide lo mismo
                que la banda, para que al llegar no salte la pantalla entera. */}
            {!diaSinCrear && !repartoResuelto ? (
                <div data-testid="dia-banda-cargando"
                    className="rounded-xl px-3.5 py-2.5 border border-border bg-muted/30 animate-pulse">
                    <div className="h-4 w-40 bg-muted rounded" />
                    <div className="h-3 w-56 bg-muted rounded mt-1.5" />
                </div>
            ) : (
            <div data-testid={`dia-banda-${estadoDeLaBanda}`}
                className={`rounded-xl px-3.5 py-2.5 flex flex-col sm:flex-row sm:items-center
                            gap-2 sm:gap-3 border ${
                    estadoDeLaBanda === 'listo' ? 'border-emerald-500/40 bg-emerald-500/5'
                        : estadoDeLaBanda === 'sin_terminar' ? 'border-brand/45 bg-brand/[0.09]'
                            : 'border-border bg-muted/30'}`}>
                <div className="min-w-0 flex-1">
                    <p className={`text-sm font-bold ${
                        estadoDeLaBanda === 'listo' ? 'text-emerald-400'
                            : estadoDeLaBanda === 'sin_terminar' ? 'text-brand'
                                : 'text-foreground/55'}`}>
                        {tituloDeLaBanda}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">{pieDeLaBanda}</p>
                </div>
                {/* Y EL BOTÓN, SOLO CUANDO HAY ALGO QUE HACER. «Terminarla» salía también el
                    día sin una sola comida, que no es terminar nada: ahí lo que toca es
                    crearla. Con el día listo no hay botón, que no hay nada pendiente. */}
                {estadoDeLaBanda === 'sin_terminar' && (
                    <button onClick={terminarla} data-testid="terminar-dieta"
                        className="flex-shrink-0 self-start sm:self-auto rounded-xl bg-brand
                                   hover:bg-brand/85 text-[#141414] text-xs font-bold
                                   px-3.5 py-2 transition-colors">
                        Terminar el día
                    </button>
                )}
                {estadoDeLaBanda === 'sin_crear' && (
                    <button onClick={terminarla} data-testid="crear-dia-inicio"
                        className="flex-shrink-0 self-start sm:self-auto rounded-xl bg-muted
                                   hover:bg-muted/70 text-foreground text-xs font-bold
                                   px-3.5 py-2 transition-colors">
                        Crear el día
                    </button>
                )}
            </div>
            )}

            <section className="space-y-3" data-testid="tu-dieta-hoy">
                <p className="caption">Tu dieta hoy</p>
                <div className="surface p-4 sm:p-5" data-testid="macros-de-hoy">
                    {/* El deslizador de CUATRO posiciones, clonado del de Mi semana (tres). */}
                    <div className="grid grid-cols-4 bg-muted rounded-xl p-1 gap-1" role="tablist"
                        data-testid="deslizador-dieta">
                        {VISTAS.map((v) => (
                            <button key={v.id} role="tab" aria-selected={vista === v.id}
                                onClick={() => elegirVista(v.id)} data-testid={`vista-${v.id}`}
                                /* `min-w-0` para que las 4 columnas sean de verdad iguales: sin él,
                                   la celda no baja del ancho de su texto y «Macros» ensancha su
                                   columna y su pastilla invadía la de «Dieta» en pantalla estrecha.
                                   Poco padding para que los labels quepan enteros sin cortarse. */
                                className={`min-w-0 px-1 py-1.5 rounded-lg text-xs sm:text-sm font-semibold text-center transition-colors
                                    ${vista === v.id ? 'bg-brand text-white shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                                {v.label}
                                {/* El punto avisa de que la dieta del día no llega a sus
                                    macros, sin sacarle de la pestaña en la que entra.

                                    EN ROJO, NO EN NARANJA (doc del 3-09, bloque 02): «que se
                                    vea a la primera; el naranja se pierde contra el fondo del
                                    selector». Y el rojo está FUERA de la regla de colores a
                                    propósito, como el aviso: verde es cuadrado, naranja es
                                    pasarse, sin color es ir por debajo, y que la dieta esté
                                    sin terminar no es ninguna de las tres.
                                    Sobre la pestaña encendida sigue yendo en blanco: ahí el
                                    fondo ya es el naranja de la casa y el rojo no se leería. */}
                                {/* Y el punto tampoco se enciende antes de tiempo: sale de
                                    la misma cuenta que la banda, así que si esperara una y el
                                    otro no, el día cuadrado empezaría con su punto rojo. */}
                                {v.id === 'dieta' && repartoResuelto && dietaNoCuadra && (
                                    <span data-testid="dieta-no-cuadra"
                                        className={`inline-block w-1.5 h-1.5 rounded-full ml-1 align-middle
                                            ${vista === v.id ? 'bg-white' : 'bg-red-500'}`} />
                                )}
                            </button>
                        ))}
                    </div>

                    {/* `flex flex-col` NO es decoracion: sin el, el hueco de 16 px que el
                        primer bloque lleva encima (`mt-4`) se ESCAPA fuera de este div -- es el
                        colapso de margenes de toda la vida -- y la medida de aqui abajo no lo
                        contaba. Resultado: el cuerpo media igual en las cuatro pestañas y la
                        tarjeta seguia dando un salto de 18 px. Con el flex, el hueco se queda
                        dentro y lo que se mide es lo que se ve.

                        Y el `min-h` de partida es la altura del bloque de los tres numeros,
                        medida a los dos anchos con `_guia/_altura_de_la_dieta_1809.js`: sirve
                        para el primer pintado, antes de que haya nada medido, y para que el
                        que entre directo a «Llevas» sin nada marcado no vea una tarjeta enana
                        que crece al tocar la pestaña de al lado. */}
                    <div ref={cuerpoRef} data-testid="cuerpo-de-la-dieta"
                        className="flex flex-col min-h-[166px]"
                        style={sueloDelCuerpo ? { minHeight: sueloDelCuerpo } : undefined}>
                    {vista === 'llevas' && nadaMarcado ? (
                        /* Sin nada marcado, Llevas no es un 0 en rojo: se dice y ya está. */
                        <p className="text-center text-sm py-8" data-testid="llevas-vacio">
                            <span className="text-foreground font-semibold">Todavía no has marcado nada.</span>
                            <br />
                            {/* «Lo que VAYAS comiendo» (punto 94), no «lo que ya te has
                                comido»: lo de arriba mira atrás y esto mira al resto del día,
                                que es lo que le queda por hacer. */}
                            <span className="text-muted-foreground">Marca abajo lo que vayas comiendo.</span>
                        </p>
                    ) : (
                        <>
                            {/* Se pintan POR MACRO y las cuatro pestañas se pintan IGUAL: el
                                nombre con su punto encima, el número grande, la palabra del
                                estado debajo y la barra. La regla de qué estado es cada cosa
                                vive entera en lib/estadoDelMacro; aquí solo se pinta.

                                EL NÚMERO VA EN BLANCO (punto 75 del 25-08). Iba en el naranja
                                de la marca, el mismo del botón de Guardar y de la pestaña
                                activa: cinco cosas distintas pintadas del mismo color, así que
                                el color no decía nada. El estado lo dirán la palabra, el punto
                                y la barra (puntos 82 y 83); el número solo dice cuánto. */}
                            <div className="grid grid-cols-3 gap-3 mt-4">
                                {['P', 'H', 'G'].map((k) => {
                                    /* EL NÚMERO GRANDE, REDONDO (Francisco, 16-09: «en Inicio
                                       a los macros haz que queden redondeados, si dice 192,2
                                       queda en 192 pero si dice 192,6 queda en 193»).

                                       Esto ACOTA la decisión del 3-09 («que muestre también en
                                       el global, así no hay desfase, tal cual lo hace Calma»),
                                       que a su vez había tirado el `Math.round` de antes. El
                                       desfase que aquella arreglaba era éste: la comida decía
                                       15,7 y el día la contaba como 16. Sigue sin volver, y por
                                       algo que no es el redondeo: la CUENTA se hace igual que
                                       siempre, a la décima -- `hayK`, `objetivoK` y el estado no
                                       se tocan --, y lo único redondo es la cifra que se pinta.
                                       Así el día no pierde ningún gramo por el camino, y dentro
                                       de la comida, que es donde se afina, la décima se queda. */
                                    const crudo = valores ? alDecima(valores[k] || 0) : null;
                                    const objetivoK = conPeri ? alDecima(conPeri[k] || 0) : 0;
                                    /* LO QUE HAY YA, que es de donde sale el estado: lo creado
                                       en Dieta y lo comido en Llevas y en Falta (Falta enseña
                                       el mismo dato del revés, así que su estado es el mismo).
                                       En Macros no hay estado: el número ES el objetivo. */
                                    const hayK = vista === 'dieta' ? alDecima(totalDieta[k] || 0)
                                        : alDecima(llevas[k] || 0);
                                    /* `redondo` va al «de 250» y a la palabra de debajo: si
                                       el número grande es entero y su referencia lleva coma, en
                                       la misma columna se leen dos redondeos distintos. */
                                    const lectura = leerMacro({ vista, hay: hayK, objetivo: objetivoK, redondo: true });
                                    /* En Falta, el negativo NO se deja en cero (doc 21-08,
                                       apartado 5): se enseña lo pasado. La bronca no existe:
                                       es un dato, y la palabra de debajo ya dice qué es. */
                                    const impreso = crudo == null ? null
                                        : (vista === 'falta' ? Math.abs(crudo) : Math.max(0, crudo));
                                    const escrito = impreso == null ? '·' : num0(impreso);
                                    /* EN EL TELÉFONO, UN NÚMERO LARGO NO CABE EN SU TERCIO
                                       (5-09). Medido a 390 px: la columna da 96 px y «219,9»
                                       ocupa 110, así que se salía por los dos lados y se
                                       juntaba con el de al lado: «169,960,1» de corrido, sin
                                       hueco. Salió con el decimal en el global, que es del
                                       3-09 (una sola regla de redondeo, la décima); con los
                                       tres números enteros no pasaba.
                                       Se baja SÓLO el que no cabe -- cinco caracteres o más,
                                       o sea tres cifras con coma -- y SÓLO en el teléfono: a
                                       36 px «219,9» ocupa 90 y le sobran 6. Los 44 px del
                                       punto 172 se quedan para todo lo demás y, a partir de
                                       `sm`, para los tres siempre: ahí la columna pasa de
                                       170 px y nunca ha faltado sitio. */
                                    // (El tamaño lo decide `lib/tamanoDelNumero`, el mismo que Nutrición: 26-09.)
                                    return (
                                        <div key={k} className="text-center" data-testid={`dieta-hoy-${vista}-${k}`}>
                                            {/* EL PUNTO, JUNTO AL NOMBRE (punto 82). Sin punto
                                                significa que no hay nada que mirar, y por eso no
                                                hace falta leyenda que aprenderse. */}
                                            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center justify-center gap-1">
                                                {NOMBRE[k]}
                                                {llevaPunto(lectura.color) && (
                                                    <span data-testid={`punto-${vista}-${k}`}
                                                        className={`w-1.5 h-1.5 rounded-full ${fondoDelMacro(lectura.color)}`} />
                                                )}
                                            </p>
                                            {/* 44 px, arriba y abajo (punto 172 del 27-08).
                                                Estaban a 34 en el móvil y 40 en el ordenador,
                                                y es en el móvil donde dice que se nota. El
                                                peso lo pone `.numero-grande`. */}
                                            <p className={`numero-grande font-data leading-none mt-1.5 text-foreground ${tamanoDelNumero(escrito)}`}>
                                                {escrito}
                                            </p>
                                            {/* CONTRA QUÉ SE MIDE, y siempre (bloque 06 de su
                                                documento: «250 / de 250 / ya lo tienes»). Va
                                                en gris y por encima del estado: es la
                                                referencia, no el juicio. */}
                                            {lectura.referencia && (
                                                <p data-testid={`de-${vista}-${k}`}
                                                    className="text-xs mt-1 text-muted-foreground">
                                                    {lectura.referencia}
                                                </p>
                                            )}
                                            {/* Y EL RENGLÓN SE QUEDA AUNQUE NO HAYA PALABRA
                                                (Francisco, 13-09, con la captura: «mira cómo
                                                los números de hidratos se levantan sobre los
                                                otros dos»).

                                                En «Llevas», ir corto no dice nada -- es lo
                                                normal a media mañana, todavía no has comido --
                                                así que ese macro se quedaba sin su línea, y
                                                con ella se subían su barra y todo lo de
                                                debajo: las tres columnas dejaban de estar a la
                                                misma altura y la de en medio parecía otra
                                                cosa. El hueco se reserva y se deja vacío. */}
                                            <p data-testid={`palabra-${vista}-${k}`} aria-hidden={!lectura.palabra}
                                                className={`text-xs ${lectura.referencia ? '' : 'mt-1'} ${claseDelMacro(lectura.color)}`}>
                                                {lectura.palabra || ' '}
                                            </p>
                                            {/* La barra, en las cuatro menos en Macros: allí no
                                                hay nada que recorrer. */}
                                            {lectura.barra && (
                                                <div className="h-1 rounded-full bg-muted mt-1.5 overflow-hidden">
                                                    <div data-testid={`barra-${vista}-${k}`}
                                                        className={`h-full rounded-full ${fondoDelMacro(lectura.barra.color)}`}
                                                        style={{ width: `${lectura.barra.largo}%` }} />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            {/* FUERA «TE HAS PASADO X g DE…» DEBAJO DE LOS NÚMEROS (Francisco, 25-09:
                                «esas 3 líneas sobran porque ya lo dice debajo del macro»). Cada celda
                                ya dice «te pasas 15» en su color; la línea lo repetía tres veces.
                                Revierte lo del punto 80, que la dejaba. */}
                            {/* «PROVISIONALES» MIENTRAS NO TERMINE EL ALTA (ficha 03 del repaso
                                del 4-09), el mismo rótulo que Mis macros: los números son los
                                de su ficha, pero se calcularon sin sus datos. Solo en Macros,
                                que es la pestaña que dice cuál es el objetivo. */}
                            {vista === 'macros' && provisionales && (
                                <p className="text-xs text-center mt-3 flex items-center justify-center gap-2 flex-wrap"
                                    data-testid="macros-provisionales">
                                    <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600 dark:text-amber-400 text-[10px] font-bold uppercase tracking-wide">
                                        Provisionales
                                    </span>
                                    <button type="button" onClick={() => navigate('/questionnaire')}
                                        className="underline font-semibold text-amber-600 dark:text-amber-400">
                                        Termina tu alta
                                    </button>
                                </p>
                            )}
                            {vista === 'llevas' ? (
                                /* Solo las comidas y el peri: los extras no se nombran aquí
                                   porque no entran en la cuenta, y verlos al lado del número
                                   sería volver a prometer que suman. */
                                <p className="text-xs text-muted-foreground text-center mt-3"
                                    data-testid="contador-llevas">
                                    {contadorDeLlevas}
                                </p>
                            ) : PIE_DE_VISTA[vista] ? (
                                <p className="text-xs text-muted-foreground text-center mt-3"
                                    data-testid={`pie-${vista}`}>
                                    {vista === 'falta' ? pieDeFalta : PIE_DE_VISTA[vista]}
                                </p>
                            ) : null}

                            {/* EL INTERRUPTOR DEL PERIENTRENO, Y SOLO EN MACROS (puntos 86 a
                                88). En Dieta, Llevas y Falta no aparece: ahí el peri ya va
                                contado como una comida más, con su fila y su casilla.

                                LA CASILLA VA DELANTE DEL TEXTO. Puesta ahí se lee como lo que
                                es -- algo que se puede desmarcar -- y no hace falta explicarlo;
                                detrás habría que contarlo, y en el móvil competiría por el
                                ancho con la frase.

                                Y al desmarcarlo se ve la cuenta: 210 + 40 = 250 de proteína.
                                Con eso se acaba el lío entre este número y el de Mis macros,
                                que enseña el mismo día sin el peri y parecía un error.

                                A LA IZQUIERDA, alineado con el resto (punto 86 de Jesús, ficha
                                13 del repaso del 4-09): iba centrado con `mx-auto`. */}
                            {vista === 'macros' && hayPeriEnElDia && (
                                <button type="button" role="checkbox" aria-checked={periDentro}
                                    onClick={() => setPeriDentro((v) => !v)}
                                    data-testid="interruptor-peri"
                                    className="mt-2 flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors">
                                    {periDentro
                                        ? <CheckSquare className="w-4 h-4 text-ok flex-shrink-0" />
                                        : <Square className="w-4 h-4 flex-shrink-0" />}
                                    <span>
                                        {periDentro
                                            ? 'Perientreno incluido'
                                            : `Perientreno aparte · ${num0(periTotal.P)} P · ${num0(periTotal.H)} H`}
                                    </span>
                                </button>
                            )}
                        </>
                    )}
                    </div>
                </div>
            </section>

            <section className="space-y-3" data-testid="marca-comidas">
                {/* SOBRE LA MARCHA, NO POR LA NOCHE (punto 171 del 27-08). Ponía «Marca lo
                    que ya te has comido», que mira al pasado, y dentro de la pestaña Llevas
                    ya pone «Marca abajo lo que vayas comiendo». Eran las dos mitades de la
                    misma pantalla diciendo cosas distintas. Manda la de dentro. */}
                <p className="caption">Marca lo que te vayas comiendo</p>

                {/* Las hechas, contraídas en una fila: la lista queda para lo que falta
                    por comer. El «Ver» las despliega (y ahí se pueden desmarcar). */}
                {hechas.length > 0 && (
                    <div className="surface p-3.5 sm:p-4 flex items-center justify-between gap-3"
                        data-testid="resumen-hechas">
                        {/* QUÉ LLEVAS, NO SOLO CUÁNTAS (bloque 07 del doc del 3-09): «3 hechas
                            151P · 107H · 49G». Ponía «3 hechas · ocultas», y para saber qué
                            llevaba había que pulsar «Ver».
                            El «· ocultas» se va: lo dice ya el botón, que pone «Ver».

                            SON LOS MACROS DE ESAS COMIDAS, SIN LOS EXTRAS (5-09). El 3-09
                            esta línea se puso con el número de la pestaña Llevas, para que
                            las dos mitades de la pantalla no dijeran cifras distintas. Ese
                            día Llevas sólo sumaba lo marcado, así que era el mismo dato; con
                            la decisión de Francisco del 5-09 («Llevas suma los extras»), ya
                            no: con una comida marcada de 40 de proteína y un extra de 24, la
                            línea decía «1 hecha 64», y esa comida no lleva 64.
                            Manda lo que la frase promete: la línea habla de las comidas
                            hechas, así que cuenta las comidas hechas. Los extras suman en
                            Llevas y se ven en su lista, con sus macros y su etiqueta. */}
                        <p className="text-sm text-muted-foreground">
                            <span className="font-bold text-foreground">
                                {hechas.length === 1 ? '1 hecha' : `${hechas.length} hechas`}
                            </span>
                            {' '}{lineaMacros(llevasDeLasComidas, false, num0)}
                        </p>
                        <button onClick={() => setVerHechas((v) => !v)} data-testid="ver-hechas"
                            className="text-sm font-semibold text-brand flex items-center gap-0.5">
                            {verHechas ? 'Ocultar' : 'Ver'} <ChevronRight className={`w-4 h-4 transition-transform ${verHechas ? 'rotate-90' : ''}`} />
                        </button>
                    </div>
                )}

                {/* UNA FILA POR TOMA, TODAS IGUALES (puntos 96, 97 y 101). El intra y el
                    post llevan su círculo a la izquierda, como las comidas, y el rayo pasa
                    a ir junto al nombre, que es donde dice algo y no donde estorba. Al
                    marcarlos no pasa nada especial: tick verde y la línea apagada.
                    Y no llevan suplemento debajo (punto 100): el intra y el post SON el
                    suplemento, y colgarles otro confunde. */}
                {filas.map(({ clave: k, esPeri }) => {
                    const marcada = !!marcadas[k];
                    if (marcada && !verHechas) return null;
                    const peri = esPeri ? periPorBloque[k] : null;
                    const tieneAlimentos = esPeri
                        ? peri.tiene
                        : (comidasGuardadas[k]?.alimentos || []).length > 0;
                    const montado = esPeri ? peri.montado : montadoPorComida[k];
                    const objetivoFila = esPeri ? peri.objetivo : reparto?.comidas?.[k];
                    const nombre = esPeri ? k : nombreComida(k, esUnica);
                    return (
                        // EL BORDE NARANJA DEL PERI SE QUEDA EN EL CONTENEDOR. Esta fila era
                        // un solo botón y llevaba el `border-l-4` puesto en él; al partirla
                        // en dos para meter la casilla de marcar (punto 96), el borde se
                        // quedó dentro del trozo que se pulsa y dejó de verse. Es lo único
                        // que distingue de un vistazo el intra y el post de las comidas.
                        // LA TARJETA YA NO ES LA FILA: dentro van la fila (casilla, nombre,
                        // macros y flecha) y, debajo, la línea del suplemento, que lleva a otro
                        // sitio y necesita ser su propio botón (punto 190). Por eso el `flex`
                        // baja un nivel y aquí queda sólo la caja.
                        // Y LA MARCA DEL PERI SE VUELVE PALABRA CUANDO LE TOCA (bloque 07).
                        // La barra de la izquierda y el chip son LA MISMA marca en dos
                        // momentos, así que no se pintan a la vez: mientras no le toca, la
                        // barra; cuando le toca, el chip «Ahora» y el borde entero, que es
                        // como lo dibuja su maqueta. El chip va solapando el borde de arriba,
                        // y por eso la caja necesita `relative`.
                        <div key={k} data-testid={`comida-hoy-${k}`}
                            className={`surface p-3.5 sm:p-4 transition-opacity relative
                                ${esPeri && k !== laDeAhora ? 'border-l-4 border-l-brand' : ''}
                                ${k === laDeAhora ? 'border border-brand' : ''}
                                ${marcada ? 'opacity-55' : ''}`}>
                            {k === laDeAhora && (
                                <span data-testid={`ahora-${k}`}
                                    className="absolute -top-2 left-3 rounded-full bg-brand px-2 py-0.5
                                               text-[10px] font-bold uppercase tracking-wide text-white">
                                    Ahora
                                </span>
                            )}
                            <div className="flex items-center gap-3">
                            {/* La casilla. Persistencia: ver el comentario de cabecera. */}
                            <button onClick={() => marcar(k)} role="checkbox" aria-checked={marcada}
                                aria-label={`${nombre}: ${marcada ? 'ya marcado' : 'marcar como tomado'}`}
                                data-testid={`marcar-${k}`} className="flex-shrink-0 p-1 -m-1">
                                {marcada
                                    ? <CheckCircle2 className="w-6 h-6 text-ok" />
                                    : <Circle className="w-6 h-6 text-muted-foreground" />}
                            </button>
                            <button onClick={() => irALaComida(k)}
                                className="flex-1 min-w-0 flex items-center gap-3 text-left group">
                                <div className="min-w-0 flex-1">
                                    <p className={`font-bold text-sm text-foreground flex items-center gap-1.5 ${marcada ? 'line-through' : ''}`}>
                                        {esPeri && <Zap className="w-3.5 h-3.5 text-brand flex-shrink-0" />}
                                        {nombre}
                                    </p>
                                    {/* Sin `truncate`: en 390 px «Sin hacer · objetivo 47 · 72 · 12»
                                        puede no caber en una línea. Que salte de línea. */}
                                    <p className="text-sm text-muted-foreground font-data">
                                        {tieneAlimentos
                                            ? lineaMacrosDeLaFila(montado, esPeri)
                                            : objetivoFila
                                                ? (esPeri ? lineaMacrosDeLaFila(objetivoFila, true)
                                                    : `Sin hacer · objetivo ${lineaMacrosDeLaFila(objetivoFila)}`)
                                                : 'Sin hacer'}
                                    </p>
                                </div>
                                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-brand transition-colors flex-shrink-0" />
                            </button>
                            </div>

                            {/* «+ Creatina» DEBAJO DE LOS MACROS (punto 174), Y SE PUEDE TOCAR
                                (punto 190 del 27-08). «Desde Inicio no se llega a Suplementos,
                                y es donde el cliente está cada día. Con el + Creatina debajo de
                                la comida 3 ya hay por dónde: tocando ahí.»

                                Por eso sale FUERA del botón de la fila y no dentro: la fila
                                lleva a Nutrición y esto lleva a Suplementos, y un botón dentro
                                de otro botón ni es HTML válido ni deja elegir destino.
                                El margen de la izquierda lo alinea con el texto de arriba, por
                                debajo de la casilla de marcar.

                                Sólo el nombre; la dosis vive en su pantalla. Y el intra y el
                                post no entran nunca: ver `suplementosPorComida`.

                                EN GRIS Y CON SU FLECHA (punto 202 del 28-08). Iba en el naranja
                                de la marca, que por la regla del 76 es correcto -- es el color de
                                lo que se toca --, pero lo gastaba en lo que menos corre: en una
                                lista donde también hay «sobran 6,5 de hidratos», lo primero que
                                se ve no puede ser la creatina. La › dice que lleva a otro sitio
                                sin gastar color. */}
                            {!esPeri && (supPorComida[k] || []).length > 0 && (
                                <button onClick={() => navigate('/dashboard/supplements')}
                                    data-testid={`suplementos-${k}`}
                                    // MÁS CLARO (Jesús, reunión del 25-09: «con otro tono, más blanco, que
                                    // contraste un poco más»): en el gris de siempre no se veían.
                                    className="mt-1 ml-9 flex items-center gap-1 text-sm text-foreground/80 text-left hover:text-foreground transition-colors">
                                    + {supPorComida[k].join(' · ')}
                                    <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" />
                                </button>
                            )}
                        </div>
                    );
                })}
            </section>

            {/* «EXTRAS DEL DÍA», debajo de las comidas y del peri: lo comido fuera de la
                dieta. Su lista y ya: no entran en ninguna de las cuatro cuentas de arriba. */}
            {/* CON EL «+», IGUAL QUE EN NUTRICIÓN (Jesús, 9-09-2026: «a extras del día le
                falta el signo más, y al desplegar recién mostrar sus campos»). Aquí salía
                el rótulo y debajo el campo abierto, así que el formulario estaba puesto
                todo el rato sin que nadie lo hubiera pedido.
                Ver la serie entera de esta decisión en `plegable`, dentro de ExtrasDelDia:
                es la quinta vuelta y conviene mirarla antes de darle otra. */}
            <ExtrasDelDia api={api} fecha={fecha} extras={extrasDia} origen="inicio" plegable compacto
                onAnadido={(e) => setExtrasDia((prev) => [...prev, e])}
                onQuitado={(id) => setExtrasDia((prev) => prev.filter((x) => x.id !== id))} />
        </>
    );
};

export default TuDietaHoy;
